import type { ChatStore } from "../Store";
import type { AwsRequestQuery } from "@/Modules/Chat/Models/Api/Chat";
import type { ChatSendAudioEventMessageSocket } from "@/Modules/Chat/Models/WebSocket";
import type { AllowedChatRoomItemType, AudioChatRoomItem } from "@/Modules/Chat/Models/Domain/Chat";
import type { IonicLocalFile } from "@/Common/Models/Domain/Ionic";
import { ChatRoomItemType, FileTypes } from "@/Modules/Chat/Models/Domain/Chat";
import { ChatRoomItemTypeMessageWebSocket, MessageTypeWebSocket } from "@/Modules/Chat/Models/WebSocket";

import { awsUploadFileRequest } from "@/Services/TherapyApi/Chat";
import AwsFileConnector from "@/Services/FileService/AwsFileConnector";

import { danger } from "@/Utils/Debug";

class SendDocumentActionHandler {
  constructor(
    private readonly chatStore: ChatStore,
    private readonly awsFileConnector = new AwsFileConnector()
  ) {}

  public async handle(file: IonicLocalFile, chatId: string) {
    this.chatStore.isUploadingFile = true;

    const awsRequestQuery: AwsRequestQuery = {
      content_type: file.mime,
      file_name: file.name,
    };

    try {
      const awsUploadRequestApiResponse = await awsUploadFileRequest(awsRequestQuery);

      if (this.isValidSize(awsUploadRequestApiResponse.max_size, file.size)) {
        await this.awsFileConnector.awsUploadFile(
          awsUploadRequestApiResponse,
          file.blob,
          this.chatStore.setFileProgress
        );

        await this.sendAudioMessageToChat(chatId, awsUploadRequestApiResponse.id, file);
      } else {
        this.handleError();
      }
    } catch (e: unknown) {
      danger(e);
      this.handleError();
    } finally {
      this.chatStore.isUploadingFile = false;
    }
  }

  private isValidSize(maxSize: number, fileSize: number) {
    return maxSize > fileSize;
  }

  private handleError() {
    this.chatStore.setFileProgress(100);
    this.chatStore.showFileError(FileTypes.AUDIO);
  }

  private sendAudioMessageToChat(
    chatId: string,
    fileId: string,
    file: IonicLocalFile
  ): Promise<AllowedChatRoomItemType> {
    return new Promise<AllowedChatRoomItemType>((resolve) => {
      const createdAt = new Date();

      const sendTextEventPayload: ChatSendAudioEventMessageSocket = {
        action: "",
        upload_id: fileId,
        size: file.size,
        length: file.msDuration,
        chat: chatId,
        content_type: file.mime,
        created: createdAt.getTime(),
        etype: ChatRoomItemTypeMessageWebSocket.AUDIO,
        extra: "",
        nonce: `chat:message:${chatId}:${this.chatStore.rooms[chatId].events.length}`,
        private: false,
        type: MessageTypeWebSocket.CHAT_ROOM_ITEM,
      };

      const event: AudioChatRoomItem = {
        id: sendTextEventPayload.nonce,
        url: "",
        size: file.size,
        length: file.msDuration,
        old: true,
        createdAt,
        type: ChatRoomItemType.OUTGOING_TEXT,
        isConfirmed: false,
        isReceived: false,
      };

      this.chatStore.rooms[chatId].events.push(event);
      this.chatStore.socketManager?.sendMessage(sendTextEventPayload);

      resolve(event);
    });
  }
}

export default SendDocumentActionHandler;
